import React, {useState} from "react";
import {Box, Button, Card, CardContent, Grid, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {CategoryChart, ProductChart, SalesCharts} from "./distributor-analytics-charts";
import {finiteNumber, formatMoney} from "./distributor-display";
import useDistributorAnalytics from "./use-distributor-analytics";
import TabTopProducts from "./tab-top-products";
import AnalyticsFilterForm from "./analytics-filter-form";
import {rankedProducts} from "./analytics-filters";
import useAnalyticsFilters from "./use-analytics-filters";

function ChartCard({title, subtitle, children}) {
    return <Card component="section" aria-label={title} sx={{height: "100%", minWidth: 0}}><CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>{subtitle}</Typography>
        {children}
    </CardContent></Card>;
}

function SecondaryCharts({data, language, t}) {
    return <Grid container spacing={3} sx={{my: 0}}>
        <Grid item xs={12} md={6} sx={{minWidth: 0}}>
            <ChartCard title={t("DistributorTopSalesAmount")} subtitle={`${t("DistributorTopSalesAmountDesc")} ${t("DistributorSalesFormula")}`}>
                <SalesCharts data={data} language={language} t={t} horizontal/>
            </ChartCard>
        </Grid>
        <Grid item xs={12} md={6} sx={{minWidth: 0}}>
            <ChartCard title={t("DistributorCategoryUnits")} subtitle={t("DistributorCategoryUnitsDesc")}>
                <CategoryChart data={data} language={language} t={t}/>
            </ChartCard>
        </Grid>
    </Grid>;
}

function AnalyticsTable({data, language, t, number}) {
    return <Card variant="outlined" sx={{mt: 3, borderRadius: 2}}><CardContent><TableContainer tabIndex={0} role="region"
        aria-label={t("WorkspaceAnalyticsTable")} sx={{border: 1, borderColor: "divider", borderRadius: 1}}>
        <Table aria-label={t("WorkspaceAnalyticsTable")} sx={{minWidth: 640}}>
        <caption>{t("AnalyticsTableOrder")}</caption>
        <TableHead><TableRow>{["ProductName", "Category", "Currency", "DistributorUnitsSold", "DistributorSalesAmount"].map(key => <TableCell key={key}>{t(key)}</TableCell>)}</TableRow></TableHead>
        <TableBody>{data.map((row, index) => <TableRow key={`${row.productTenantId || row.productId || index}:${row.currency || "unknown"}`}>
            <TableCell>{row.productName || t("DistributorMetricUnavailable")}</TableCell>
            <TableCell>{row.category ? t(row.category) : t("DistributorCategoryUnknown")}</TableCell>
            <TableCell>{row.currency || t("DistributorCurrencyUnavailable")}</TableCell>
            <TableCell>{number(row.totalCount)}</TableCell>
            <TableCell>{formatMoney(row.totalRevenue, row.currency, language) ?? number(row.totalRevenue)}
                {finiteNumber(row.totalRevenue) && formatMoney(row.totalRevenue, row.currency, language) === null &&
                    <Typography variant="caption" display="block" color="text.secondary">{t("DistributorCurrencyUnavailable")}</Typography>}
            </TableCell>
        </TableRow>)}</TableBody>
    </Table></TableContainer></CardContent></Card>;
}

function AnalyticsPanel({tenantId}) {
    const {t, i18n} = useTranslation();
    const filters = useAnalyticsFilters();
    const {applied} = filters;
    const [type, setType] = useState("bar");
    const {status, data, error, reload} = useDistributorAnalytics(tenantId, i18n.language, applied);
    const number = value => finiteNumber(value) ? new Intl.NumberFormat(i18n.language || "en").format(value) : t("DistributorMetricUnavailable");
    const refresh = () => { if (!filters.applyLatest()) reload(); };
    const rows = data && rankedProducts(data, applied.rankBy);
    const title = t(applied.rankBy === "SALES_AMOUNT" ? "AnalyticsTopSales" : "TopSellingProducts");
    const pieAvailable = applied.rankBy === "UNITS" && Number(applied.limit) <= 10;

    return <Box>
        <Box display="flex" flexWrap="wrap" gap={2} justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">{t("DistributionAnalytics")}</Typography>
            <Button variant="outlined" disabled={status === "loading"} onClick={refresh}>{t("Refresh")}</Button>
        </Box>
        <AnalyticsFilterForm {...filters} t={t}/>
        <Box sx={{display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2}}>
            <Typography variant="body2" role="status" sx={{flex: 1, minWidth: 180}}>
                {t("AnalyticsApplied")}: {applied.from} — {applied.to} · {t(applied.rankBy === "SALES_AMOUNT" ? "DistributorSalesAmount" : "DistributorUnitsSold")} · {t("NumberOfProducts")}: {applied.limit}
            </Typography>
            <TextField size="small" select label={t("ChartType")} value={type === "pie" && !pieAvailable ? "bar" : type}
                sx={{minWidth: 180, maxWidth: "100%"}} onChange={event => setType(event.target.value)}>
                <MenuItem value="bar">{t("AnalyticsRankedBars")}</MenuItem>
                <MenuItem value="pie" disabled={!pieAvailable}>{t("PieChart")}</MenuItem>
            </TextField>
        </Box>
        <TabTopProducts title={`${title} (${data === null ? "—" : number(data.length)})`} status={status}
            error={error} reload={reload} data={rows}>
            {applied.rankBy === "SALES_AMOUNT" ? <SalesCharts data={rows || []} language={i18n.language} t={t} scrollColumns/>
                : <ProductChart data={rows || []} type={type === "pie" && pieAvailable ? "pie" : "bar"} scrollColumns language={i18n.language} t={t}/>}
        </TabTopProducts>
        {status === "success" && data?.length > 0 && <>
            <SecondaryCharts data={rows} language={i18n.language} t={t}/>
            <AnalyticsTable data={rows} language={i18n.language} t={t} number={number}/>
        </>}
    </Box>;
}

export default function TabAnalytics({tabValue, tenantId, userIdentity = ""}) {
    const {i18n} = useTranslation();
    // Mount only in the active view; identities and languages cannot reuse the previous tenant's result.
    return <div role="tabpanel" hidden={tabValue !== 1} id="distributor-tabpanel-1" aria-labelledby="distributor-tab-1">
        {tabValue === 1 && <>
            {tenantId && <AnalyticsPanel key={`${tenantId}:${userIdentity}:${i18n.language}`} tenantId={tenantId}/>}
        </>}
    </div>;
}
